import React from "react";
import ImageInput from "../components/ImageInput";

/* ── Shared defaults (editable once loaded into the form) ───────────── */
export const DEFAULT_AFFILIATE =
  "https://click.validclick.net/rdr2.php?aff=pennywiseclub&q=non+fog+glasses&cmp=112845&ref=%5BREF%5D";
export const DEFAULT_THEME_START = "#61a99f";
export const DEFAULT_THEME_END = "#3d6e68";
export const DEFAULT_ACCENT = "#61a99f";
export const DEFAULT_TAKE =
  "This collection stood out for delivering exceptional value while maintaining a premium aesthetic.";
export const DEFAULT_BULLETS = [
  "Summer-ready styling",
  "Comfort-focused design",
  "Strong customer popularity",
];

export const TYPE_LABELS = { "grid-guide": "Grid Guide", listicle: "Ranked Listicle" };

const seedCard = (label) => ({
  img: "",
  tag: "Brand",
  title: "",
  url: "",
  description: `Optimized for ${label ? label.toLowerCase() : "this"} proportions and current trends.`,
});

const gridGuideDefaults = () => ({
  eyebrow: "PERSONALIZED STYLE GUIDE • 2026",
  title: "What's The Best Style For You?",
  subtitle:
    "Select a category below for curated recommendations from leading brands.",
  categories: ["Round", "Square", "Oval", "Heart", "Diamond"].map((label) => ({
    label,
    cards: [seedCard(label)],
  })),
});

const listicleDefaults = () => ({
  eyebrow: "EDITOR'S GUIDE • 2026",
  title: "10 Products Everyone Seems To Be Loving",
  subtitle:
    "We researched the products dominating social media and wishlists this season.",
  metaBadges: ["Editor Reviewed", "Style Trends", "Value Scored"],
  howWePickedTitle: "How We Picked",
  howWePickedText:
    "We evaluated trends, social engagement, price-to-quality ratio, comfort, and consumer sentiment to identify the collections worth considering.",
  defaultEditorTake: DEFAULT_TAKE,
  picks: [1, 2, 3].map(() => ({
    img: "",
    title: "",
    badge: "Best Overall",
    price: "$0.00",
    description: "",
    take: DEFAULT_TAKE,
    bullets: [...DEFAULT_BULLETS],
    url: "",
  })),
  tableTitle: "Quick Comparison",
  tableRows: [
    { cells: ["Brand", "Best For", "Starting Price"] },
    { cells: ["Brand A", "Best Overall Value", "$19.95+"] },
    { cells: ["Brand B", "Premium Pick", "$95+"] },
  ],
  verdictTitle: "Our Final Verdict",
  verdictText:
    "If you want the best combination of style, value, and variety, these collections currently offer one of the strongest propositions in the market.",
  verdictButtonText: "Shop Now",
});

export const buildInitial = (type) => ({
  slug: "",
  type,
  metaTitle: "",
  canonicalSlug: "",
  date: new Date().toISOString().split("T")[0],
  affiliateUrl: DEFAULT_AFFILIATE,
  themeStart: DEFAULT_THEME_START,
  themeEnd: DEFAULT_THEME_END,
  accent: DEFAULT_ACCENT,
  ...(type === "grid-guide" ? gridGuideDefaults() : listicleDefaults()),
});

/* ── Small reusable input pieces ───────────────────────────────────── */
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls =
  "mt-1 w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-[#4d8a82]";

/**
 * The full page-editing form (all sections + live preview). Purely controlled:
 * the parent owns `formData` / `setFormData` and provides submit wiring. Reused
 * by PageEditor (single create/edit) and PageCsvImport (per-row bulk editing).
 */
export default function PageForm({
  formData,
  setFormData,
  slugError = "",
  onSlugChange,
  slugDisabled = false,
  slugRef,
  onSubmit,
  footer,
  submitLabel = "Publish Page",
  submitting = false,
}) {
  const pageType = formData.type;

  /* ── generic field change ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  /* ── categories (grid-guide) ── */
  const updateCategory = (ci, patch) =>
    setFormData((prev) => {
      const categories = [...prev.categories];
      categories[ci] = { ...categories[ci], ...patch };
      return { ...prev, categories };
    });
  const updateCard = (ci, ki, patch) =>
    setFormData((prev) => {
      const categories = [...prev.categories];
      const cards = [...categories[ci].cards];
      cards[ki] = { ...cards[ki], ...patch };
      categories[ci] = { ...categories[ci], cards };
      return { ...prev, categories };
    });
  const addCategory = () =>
    setField("categories", [...formData.categories, { label: "New Category", cards: [seedCard("")] }]);
  const removeCategory = (ci) =>
    setField("categories", formData.categories.filter((_, i) => i !== ci));
  const addCard = (ci) =>
    updateCategory(ci, { cards: [...formData.categories[ci].cards, seedCard(formData.categories[ci].label)] });
  const removeCard = (ci, ki) =>
    updateCategory(ci, { cards: formData.categories[ci].cards.filter((_, i) => i !== ki) });

  /* ── picks (listicle) ── */
  const updatePick = (pi, patch) =>
    setFormData((prev) => {
      const picks = [...prev.picks];
      picks[pi] = { ...picks[pi], ...patch };
      return { ...prev, picks };
    });
  const addPick = () =>
    setField("picks", [
      ...formData.picks,
      {
        img: "",
        title: "",
        badge: "",
        price: "",
        description: "",
        take: formData.defaultEditorTake || "",
        bullets: [...DEFAULT_BULLETS],
        url: "",
      },
    ]);
  const removePick = (pi) => setField("picks", formData.picks.filter((_, i) => i !== pi));
  const applyTakeToAll = () =>
    setField(
      "picks",
      formData.picks.map((p) => ({ ...p, take: formData.defaultEditorTake || "" }))
    );
  const updateBullet = (pi, bi, value) => {
    const bullets = [...(formData.picks[pi].bullets || [])];
    bullets[bi] = value;
    updatePick(pi, { bullets });
  };
  const addBullet = (pi) => updatePick(pi, { bullets: [...(formData.picks[pi].bullets || []), ""] });
  const removeBullet = (pi, bi) =>
    updatePick(pi, { bullets: (formData.picks[pi].bullets || []).filter((_, i) => i !== bi) });

  /* ── meta badges (listicle) ── */
  const updateBadge = (i, value) => {
    const metaBadges = [...(formData.metaBadges || [])];
    metaBadges[i] = value;
    setField("metaBadges", metaBadges);
  };
  const addBadge = () => setField("metaBadges", [...(formData.metaBadges || []), ""]);
  const removeBadge = (i) => setField("metaBadges", (formData.metaBadges || []).filter((_, x) => x !== i));

  /* ── comparison table (listicle) ── */
  const updateCell = (ri, ci, value) => {
    const tableRows = formData.tableRows.map((r) => ({ cells: [...r.cells] }));
    tableRows[ri].cells[ci] = value;
    setField("tableRows", tableRows);
  };
  const addTableRow = () => {
    const cols = formData.tableRows[0]?.cells.length || 3;
    setField("tableRows", [...formData.tableRows, { cells: Array(cols).fill("") }]);
  };
  const removeTableRow = (ri) => setField("tableRows", formData.tableRows.filter((_, i) => i !== ri));
  const addTableColumn = () =>
    setField("tableRows", formData.tableRows.map((r) => ({ cells: [...r.cells, ""] })));
  const removeTableColumn = () =>
    setField(
      "tableRows",
      formData.tableRows.map((r) => ({ cells: r.cells.slice(0, Math.max(1, r.cells.length - 1)) }))
    );

  const heroGradient = `linear-gradient(135deg, ${formData.themeStart}, ${formData.themeEnd})`;

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* ── Core / SEO / theme ── */}
      <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h2 className="text-lg font-bold mb-4 text-gray-800">Page Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug {slugDisabled ? "(Read-only)" : ""}
            </label>
            <div className="flex items-center">
              <span className="text-gray-400 text-sm mr-1">/stores/</span>
              <input
                ref={slugRef}
                type="text"
                name="slug"
                value={formData.slug || ""}
                onChange={(e) => {
                  handleChange(e);
                  onSlugChange && onSlugChange();
                }}
                disabled={slugDisabled}
                placeholder="e.g. summer-sunglasses-guide"
                className={`mt-1 w-full border p-2 rounded disabled:bg-gray-100 ${
                  slugError ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
              />
            </div>
            {slugError && <p className="text-red-500 text-xs mt-1 font-semibold">{slugError}</p>}
          </div>
          <Field label="Date">
            <input type="date" name="date" value={formData.date} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="Meta Title (browser tab / SEO)">
            <input type="text" name="metaTitle" value={formData.metaTitle || ""} onChange={handleChange} className={inputCls} placeholder="Optional — defaults to the page title" />
          </Field>
          <Field label="Canonical Store Slug (optional)">
            <input type="text" name="canonicalSlug" value={formData.canonicalSlug || ""} onChange={handleChange} className={inputCls} placeholder="e.g. zennioptical.com" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Default Affiliate / Outbound Link (used by cards & CTAs without their own link)">
              <input type="url" name="affiliateUrl" value={formData.affiliateUrl || ""} onChange={handleChange} className={inputCls} />
            </Field>
          </div>
          <Field label="Theme Gradient Start">
            <div className="flex items-center gap-2">
              <input type="color" name="themeStart" value={formData.themeStart} onChange={handleChange} className="h-10 w-14 border rounded" />
              <input type="text" name="themeStart" value={formData.themeStart} onChange={handleChange} className={inputCls} />
            </div>
          </Field>
          <Field label="Theme Gradient End">
            <div className="flex items-center gap-2">
              <input type="color" name="themeEnd" value={formData.themeEnd} onChange={handleChange} className="h-10 w-14 border rounded" />
              <input type="text" name="themeEnd" value={formData.themeEnd} onChange={handleChange} className={inputCls} />
            </div>
          </Field>
          <Field label="Accent Color">
            <div className="flex items-center gap-2">
              <input type="color" name="accent" value={formData.accent} onChange={handleChange} className="h-10 w-14 border rounded" />
              <input type="text" name="accent" value={formData.accent} onChange={handleChange} className={inputCls} />
            </div>
          </Field>
        </div>
      </section>

      {/* ── Hero ── */}
      <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h2 className="text-lg font-bold mb-4 text-gray-800">Hero</h2>
        <div className="grid grid-cols-1 gap-4">
          <Field label="Eyebrow / Kicker">
            <input type="text" name="eyebrow" value={formData.eyebrow || ""} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="Title">
            <input type="text" name="title" value={formData.title || ""} onChange={handleChange} required className={inputCls} />
          </Field>
          <Field label="Subtitle">
            <textarea rows="2" name="subtitle" value={formData.subtitle || ""} onChange={handleChange} className={`${inputCls} resize-y`} />
          </Field>
        </div>
        {/* Hero preview */}
        <div className="mt-4 rounded-xl overflow-hidden text-white p-8" style={{ background: heroGradient }}>
          <div className="font-extrabold text-xs tracking-wide opacity-90">{formData.eyebrow}</div>
          <div className="text-3xl font-bold mt-2">{formData.title || "Your title"}</div>
          <div className="opacity-90 mt-2 text-sm">{formData.subtitle}</div>
          {pageType === "listicle" && (
            <div className="flex gap-2 flex-wrap mt-4">
              {(formData.metaBadges || []).map((b, i) => (
                <span key={i} className="bg-white/15 px-3 py-1 rounded-full text-xs">✓ {b}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── GRID-GUIDE editor ── */}
      {pageType === "grid-guide" && (
        <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">Categories & Cards</h2>
            <button type="button" onClick={addCategory} className="bg-[#4d8a82] text-white px-3 py-1.5 rounded text-sm hover:bg-[#3d6e68]">
              + Add Category
            </button>
          </div>
          <div className="space-y-6">
            {(formData.categories || []).map((cat, ci) => (
              <div key={ci} className="border border-[#c8e6e3] rounded-lg p-4 bg-[#f7fbfa]">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    value={cat.label}
                    onChange={(e) => updateCategory(ci, { label: e.target.value })}
                    className="font-bold text-[#4d8a82] border-b border-dashed border-[#a0d2cc] bg-transparent flex-1 p-1 focus:outline-none"
                    placeholder="Category label"
                  />
                  <button type="button" onClick={() => addCard(ci)} className="text-[#4d8a82] text-sm border border-[#a0d2cc] rounded px-2 py-1 hover:bg-[#eef6f5]">
                    + Card
                  </button>
                  <button type="button" onClick={() => removeCategory(ci)} className="text-red-500 text-sm hover:text-red-700">
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(cat.cards || []).map((card, ki) => (
                    <div key={ki} className="border border-gray-200 rounded p-3 bg-white relative">
                      <button type="button" onClick={() => removeCard(ci, ki)} className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xs">✕</button>
                      <div className="flex gap-3">
                        <img
                          src={card.img || "https://placehold.co/80x80?text=IMG"}
                          alt=""
                          className="w-16 h-16 object-contain bg-gray-50 rounded border"
                        />
                        <div className="flex-1 space-y-2">
                          <input type="text" value={card.title} onChange={(e) => updateCard(ci, ki, { title: e.target.value })} className="w-full border border-gray-300 rounded p-1 text-sm" placeholder="Product title" />
                          <input type="text" value={card.tag} onChange={(e) => updateCard(ci, ki, { tag: e.target.value })} className="w-full border border-gray-300 rounded p-1 text-sm" placeholder="Brand / tag" />
                        </div>
                      </div>
                      <ImageInput
                        className="mt-2"
                        value={card.img}
                        onChange={(url) => updateCard(ci, ki, { img: url })}
                        keyHint={`${formData.slug || "card"}-${cat.label || "cat"}-${card.title || ki}`}
                        placeholder="Image URL or upload"
                      />
                      <input type="url" value={card.url} onChange={(e) => updateCard(ci, ki, { url: e.target.value })} className="w-full border border-gray-300 rounded p-1 text-sm mt-2" placeholder="Link (blank = default affiliate)" />
                      <textarea rows="2" value={card.description} onChange={(e) => updateCard(ci, ki, { description: e.target.value })} className="w-full border border-gray-300 rounded p-1 text-sm mt-2 resize-y" placeholder="Card description" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── LISTICLE editor ── */}
      {pageType === "listicle" && (
        <>
          <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Hero Badges & Intro</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Badges</label>
              <div className="flex flex-wrap gap-2">
                {(formData.metaBadges || []).map((b, i) => (
                  <div key={i} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2">
                    <input type="text" value={b} onChange={(e) => updateBadge(i, e.target.value)} className="p-1 text-sm bg-transparent focus:outline-none" />
                    <button type="button" onClick={() => removeBadge(i)} className="text-gray-400 hover:text-red-600 text-xs">✕</button>
                  </div>
                ))}
                <button type="button" onClick={addBadge} className="text-[#4d8a82] text-sm border border-[#a0d2cc] rounded px-2 py-1 hover:bg-[#eef6f5]">+ Badge</button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <Field label="'How We Picked' Heading">
                <input type="text" name="howWePickedTitle" value={formData.howWePickedTitle || ""} onChange={handleChange} className={inputCls} />
              </Field>
              <Field label="'How We Picked' Text">
                <textarea rows="3" name="howWePickedText" value={formData.howWePickedText || ""} onChange={handleChange} className={`${inputCls} resize-y`} />
              </Field>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-lg font-bold mb-2 text-gray-800">Default Editor's Take</h2>
            <p className="text-gray-500 text-sm mb-3">
              New products inherit this text. Use the button to apply it to every existing product too.
            </p>
            <textarea rows="2" name="defaultEditorTake" value={formData.defaultEditorTake || ""} onChange={handleChange} className={`${inputCls} resize-y`} />
            <button type="button" onClick={applyTakeToAll} className="mt-3 bg-gray-800 text-white px-4 py-2 rounded text-sm hover:bg-gray-900">
              Apply default take to all products
            </button>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Ranked Products</h2>
              <button type="button" onClick={addPick} className="bg-[#4d8a82] text-white px-3 py-1.5 rounded text-sm hover:bg-[#3d6e68]">+ Add Product</button>
            </div>
            <div className="space-y-5">
              {(formData.picks || []).map((pick, pi) => (
                <div key={pi} className="border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl font-extrabold" style={{ color: formData.accent }}>#{pi + 1}</span>
                    <button type="button" onClick={() => removePick(pi)} className="text-red-500 text-sm hover:text-red-700">Remove</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="text" value={pick.title} onChange={(e) => updatePick(pi, { title: e.target.value })} className={inputCls} placeholder="Product title" />
                    <input type="text" value={pick.badge} onChange={(e) => updatePick(pi, { badge: e.target.value })} className={inputCls} placeholder="Badge (e.g. Best Overall)" />
                    <input type="text" value={pick.price} onChange={(e) => updatePick(pi, { price: e.target.value })} className={inputCls} placeholder="Price (e.g. $19.95)" />
                    <input type="url" value={pick.url} onChange={(e) => updatePick(pi, { url: e.target.value })} className={inputCls} placeholder="Link (blank = default affiliate)" />
                    <ImageInput
                      className="md:col-span-2"
                      value={pick.img}
                      onChange={(url) => updatePick(pi, { img: url })}
                      keyHint={`${formData.slug || "pick"}-${pick.title || pi}`}
                      placeholder="Image URL or upload"
                    />
                    <textarea rows="2" value={pick.description} onChange={(e) => updatePick(pi, { description: e.target.value })} className={`${inputCls} md:col-span-2 resize-y`} placeholder="Description" />
                    <textarea rows="2" value={pick.take} onChange={(e) => updatePick(pi, { take: e.target.value })} className={`${inputCls} md:col-span-2 resize-y`} placeholder="Editor's Take" />
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bullet Points</label>
                    <div className="space-y-1">
                      {(pick.bullets || []).map((b, bi) => (
                        <div key={bi} className="flex items-center gap-2">
                          <input type="text" value={b} onChange={(e) => updateBullet(pi, bi, e.target.value)} className="flex-1 border border-gray-300 rounded p-1 text-sm" placeholder="Bullet" />
                          <button type="button" onClick={() => removeBullet(pi, bi)} className="text-gray-400 hover:text-red-600 text-xs">✕</button>
                        </div>
                      ))}
                      <button type="button" onClick={() => addBullet(pi)} className="text-[#4d8a82] text-sm hover:underline">+ Add bullet</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Comparison Table</h2>
            <Field label="Table Title">
              <input type="text" name="tableTitle" value={formData.tableTitle || ""} onChange={handleChange} className={inputCls} />
            </Field>
            <div className="overflow-x-auto mt-3">
              <table className="border-collapse">
                <tbody>
                  {(formData.tableRows || []).map((row, ri) => (
                    <tr key={ri}>
                      {row.cells.map((cell, ci) => (
                        <td key={ci} className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={cell}
                            onChange={(e) => updateCell(ri, ci, e.target.value)}
                            className={`p-1 text-sm w-36 ${ri === 0 ? "font-bold" : ""}`}
                            placeholder={ri === 0 ? "Header" : "Cell"}
                          />
                        </td>
                      ))}
                      <td className="pl-2">
                        <button type="button" onClick={() => removeTableRow(ri)} className="text-gray-400 hover:text-red-600 text-xs">✕ row</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-3 mt-3 text-sm">
              <button type="button" onClick={addTableRow} className="text-[#4d8a82] hover:underline">+ Row</button>
              <button type="button" onClick={addTableColumn} className="text-[#4d8a82] hover:underline">+ Column</button>
              <button type="button" onClick={removeTableColumn} className="text-gray-500 hover:underline">− Column</button>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Final Verdict</h2>
            <div className="grid grid-cols-1 gap-4">
              <Field label="Verdict Heading">
                <input type="text" name="verdictTitle" value={formData.verdictTitle || ""} onChange={handleChange} className={inputCls} />
              </Field>
              <Field label="Verdict Text">
                <textarea rows="3" name="verdictText" value={formData.verdictText || ""} onChange={handleChange} className={`${inputCls} resize-y`} />
              </Field>
              <Field label="Verdict Button Text">
                <input type="text" name="verdictButtonText" value={formData.verdictButtonText || ""} onChange={handleChange} className={inputCls} />
              </Field>
            </div>
          </section>
        </>
      )}

      {/* ── Live visual preview ── */}
      <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h2 className="text-lg font-bold mb-2 text-gray-800">Live Preview</h2>
        <p className="text-gray-500 text-sm mb-4">Approximate rendering of the published page.</p>
        <PagePreview data={formData} heroGradient={heroGradient} />
      </section>

      {footer !== undefined ? (
        footer
      ) : (
        <div className="pb-10">
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#4d8a82] text-white font-bold py-4 text-xl rounded-xl hover:bg-[#3d6e68] transition disabled:opacity-60"
          >
            {submitting ? "Saving..." : submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}

/* ── Preview (read-only, mirrors the frontend layouts) ─────────────── */
export function PagePreview({ data, heroGradient }) {
  const accent = data.accent || DEFAULT_ACCENT;
  const linkOf = (u) => u || data.affiliateUrl;
  const gradient =
    heroGradient || `linear-gradient(135deg, ${data.themeStart}, ${data.themeEnd})`;

  return (
    <div className="border rounded-xl overflow-hidden bg-[#f7f8fb]">
      {/* Hero */}
      <div className="text-white p-8" style={{ background: gradient }}>
        <div className="font-extrabold text-xs tracking-wide opacity-90">{data.eyebrow}</div>
        <div className="text-2xl md:text-4xl font-bold mt-2">{data.title || "Your title"}</div>
        <div className="opacity-90 mt-2 text-sm max-w-2xl">{data.subtitle}</div>
      </div>

      {data.type === "grid-guide" && (
        <div className="p-5 space-y-6">
          {(data.categories || []).map((cat, ci) => (
            <div key={ci} className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="text-lg font-bold mb-3">{cat.label} </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(cat.cards || []).map((card, ki) => (
                  <a key={ki} href={linkOf(card.url)} target="_blank" rel="noopener noreferrer" className="block bg-[#f8fafc] rounded-xl p-3 hover:shadow">
                    <img src={card.img || "https://placehold.co/160x120?text=IMG"} alt={card.title} className="w-full h-24 object-contain bg-white rounded" />
                    <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mt-2" style={{ background: "#c8e6e3", color: "#4d8a82" }}>{card.tag}</span>
                    <div className="font-semibold text-sm mt-1">{card.title || "Product"}</div>
                    <div className="text-xs text-gray-500">{card.description}</div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {data.type === "listicle" && (
        <div className="p-5 space-y-4">
          {data.howWePickedTitle && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold">{data.howWePickedTitle}</h3>
              <p className="text-sm text-gray-600 mt-1">{data.howWePickedText}</p>
            </div>
          )}
          {(data.picks || []).map((pick, pi) => (
            <a key={pi} href={linkOf(pick.url)} target="_blank" rel="noopener noreferrer" className="grid grid-cols-1 md:grid-cols-[200px_1fr] bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow">
              <img src={pick.img || "https://placehold.co/200x200?text=IMG"} alt={pick.title} className="w-full h-40 md:h-full object-cover" />
              <div className="p-4">
                <div className="text-2xl font-extrabold" style={{ color: accent }}>#{pi + 1}</div>
                <div className="text-lg font-bold">{pick.title || "Product"}</div>
                {pick.badge && <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full mt-1" style={{ background: "#c8e6e3", color: "#4d8a82" }}>{pick.badge}</span>}
                {pick.price && <div className="font-bold mt-1">Starting at {pick.price}</div>}
                <p className="text-sm text-gray-600 mt-1">{pick.description}</p>
                {pick.take && (
                  <div className="bg-[#eef7f6] p-2 rounded mt-2 text-sm"><b>Editor's Take:</b> {pick.take}</div>
                )}
                <ul className="list-disc pl-5 text-sm text-gray-600 mt-2">
                  {(pick.bullets || []).map((b, bi) => <li key={bi}>{b}</li>)}
                </ul>
              </div>
            </a>
          ))}
          {data.tableRows && data.tableRows.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm overflow-x-auto">
              <h3 className="font-bold mb-2">{data.tableTitle}</h3>
              <table className="w-full text-sm border-collapse">
                <tbody>
                  {data.tableRows.map((row, ri) => (
                    <tr key={ri}>
                      {row.cells.map((cell, ci) => (
                        <td key={ci} className={`border-b border-gray-200 p-2 ${ri === 0 ? "font-bold bg-gray-50" : ""}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="rounded-2xl p-6 text-white text-center" style={{ background: gradient }}>
            <h3 className="text-xl font-bold">{data.verdictTitle}</h3>
            <p className="mt-2 text-sm opacity-90">{data.verdictText}</p>
            <span className="inline-block bg-white text-[#4d8a82] font-bold px-6 py-2 rounded-full mt-3">{data.verdictButtonText || "Shop Now"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
